import { TargetPlatforms, TargetPlatformsDisplayName } from "../models/targetPlatforms";

type SummaryWebsite = Readonly<{
  href: string;
  label: string;
}>;

export type GameConceptSummaryData = Readonly<{
  description?: string;
  playableOn: ReadonlyArray<string>;
  genreCode?: string;
  otherGenre?: string;
  releaseDate?: string;
  website?: SummaryWebsite;
}>;

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const getWebsite = (values: ReadonlyArray<unknown>): SummaryWebsite | undefined => {
  for (const value of values) {
    const website = asNonEmptyString(value);
    if (!website) {
      continue;
    }

    const href = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    try {
      const parsedUrl = new URL(href);
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
        continue;
      }

      return {
        href: parsedUrl.toString(),
        label: website.replace(/^https?:\/\//i, "").replace(/\/$/, ""),
      };
    } catch {
      continue;
    }
  }

  return undefined;
};

const getPlatformLabel = (targetPlatform: Record<string, unknown>): string | undefined => {
  if (targetPlatform.selected !== true) {
    return undefined;
  }

  const platform = asNonEmptyString(targetPlatform.platform);
  if (!platform) {
    return undefined;
  }

  if (platform === TargetPlatforms.other) {
    return asNonEmptyString(targetPlatform.otherPlatformName);
  }

  return TargetPlatformsDisplayName[platform as keyof typeof TargetPlatformsDisplayName];
};

export const getGameConceptSummaryData = (
  formData: Record<string, Record<string, unknown>> | null | undefined
): GameConceptSummaryData => {
  const details = asRecord(formData?.details);
  const gameExpectations = asRecord(formData?.gameExpectations);
  const targetPlatformsValue = gameExpectations?.targetPlatforms;
  const targetPlatforms = Array.isArray(targetPlatformsValue)
    ? targetPlatformsValue
        .map(asRecord)
        .filter((value): value is Record<string, unknown> => value !== undefined)
    : [];

  const additionalLinksValue = details?.additionalLinks;
  const additionalLinks = Array.isArray(additionalLinksValue) ? additionalLinksValue : [];
  const releaseDate =
    asNonEmptyString(details?.targetReleaseDate) ??
    targetPlatforms
      .filter(targetPlatform => targetPlatform.selected === true)
      .map(targetPlatform => asNonEmptyString(targetPlatform.releaseDate))
      .filter((value): value is string => value !== undefined)
      .sort()[0];
  const genre = details?.genre;

  return {
    description: asNonEmptyString(details?.gameplayDetails) ?? asNonEmptyString(details?.hook),
    playableOn: targetPlatforms
      .map(getPlatformLabel)
      .filter((value): value is string => value !== undefined),
    genreCode:
      (typeof genre === "number" && genre !== 0) ||
      (typeof genre === "string" && genre !== "" && genre !== "0")
        ? String(genre)
        : undefined,
    otherGenre: asNonEmptyString(details?.otherGenre),
    releaseDate,
    website: getWebsite([...additionalLinks, details?.steamLink, details?.itchIoLink]),
  };
};

export const formatGameConceptSummaryDate = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return undefined;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};
